// ============================================================================
// ECHARTS SETUP — TREE-SHAKEN IMPORTS + DAS THEMES
// ============================================================================
// Imported once by the EChart wrapper. It:
// 1. Registers only the chart types / components actively used (everything
//    else is tree-shaken out of the bundle). Add new chart types here when
//    a future report needs them.
// 2. Registers 'das-light' / 'das-dark' themes so charts match the active
//    application theme.
// ============================================================================

import * as echarts from 'echarts/core';
import { BarChart, PieChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  DataZoomComponent,
  ToolboxComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

// ---------------------------------------------------------------------------
// Chart & component registration (one-time)
// ---------------------------------------------------------------------------

echarts.use([
  BarChart,
  PieChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  DataZoomComponent,
  ToolboxComponent,
  CanvasRenderer,
]);

// ---------------------------------------------------------------------------
// Shared palette
// ---------------------------------------------------------------------------
// This is the default series palette used when widgets don't set itemStyle
// explicitly. Per-data-point coloring (e.g., status color mapping in
// Submission Trend) is applied in each widget, not here.

const PALETTE = [
  '#2563eb', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#dc2626', // red
  '#0ea5e9', // sky
  '#a855f7', // purple
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
  '#64748b', // slate
];

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

echarts.registerTheme('das-light', {
  color: PALETTE,
  backgroundColor: 'transparent',
  textStyle: { color: '#1e293b', fontFamily: 'inherit' },
  title: { textStyle: { color: '#1e293b' } },
  legend: { textStyle: { color: '#64748b', fontSize: 11 } },
  tooltip: {
    backgroundColor: '#ffffff',
    borderColor: '#e2e8f0',
    borderWidth: 1,
    textStyle: { color: '#1e293b', fontSize: 12 },
    extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 6px;',
  },
  axisPointer: { lineStyle: { color: '#94a3b8' } },
  categoryAxis: {
    axisLine: { lineStyle: { color: '#cbd5e1' } },
    axisTick: { lineStyle: { color: '#cbd5e1' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
    splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
  },
  valueAxis: {
    axisLine: { lineStyle: { color: '#cbd5e1' } },
    axisTick: { lineStyle: { color: '#cbd5e1' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
    splitLine: { lineStyle: { color: '#f1f5f9', type: 'dashed' } },
  },
});

echarts.registerTheme('das-dark', {
  color: PALETTE,
  backgroundColor: 'transparent',
  textStyle: { color: '#e2e8f0', fontFamily: 'inherit' },
  title: { textStyle: { color: '#e2e8f0' } },
  legend: { textStyle: { color: '#94a3b8', fontSize: 11 } },
  tooltip: {
    backgroundColor: '#1e293b',
    borderColor: '#334155',
    borderWidth: 1,
    textStyle: { color: '#e2e8f0', fontSize: 12 },
    extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.4); border-radius: 6px;',
  },
  axisPointer: { lineStyle: { color: '#64748b' } },
  categoryAxis: {
    axisLine: { lineStyle: { color: '#334155' } },
    axisTick: { lineStyle: { color: '#334155' } },
    axisLabel: { color: '#94a3b8', fontSize: 11 },
    splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } },
  },
  valueAxis: {
    axisLine: { lineStyle: { color: '#334155' } },
    axisTick: { lineStyle: { color: '#334155' } },
    axisLabel: { color: '#94a3b8', fontSize: 11 },
    splitLine: { lineStyle: { color: '#1e293b', type: 'dashed' } },
  },
});

// Re-export echarts so the wrapper (and callers) share the same tree-shaken instance.
export { echarts };
export const DAS_PALETTE = PALETTE;
